import React from "react";

interface YAxisScaleProps {
	domain: { min: number; max: number };
	labelFormat: string;
	height?: number;
	paddingTop?: number;
	paddingBottom?: number;
	formatDataLabel: (value: number, format: string, isAxisLabel?: boolean) => string;
	fontSize?: string | number;
	bottomTickOffset?: number;
}

export const YAxisScale: React.FC<YAxisScaleProps> = ({ domain, labelFormat, height = 285, formatDataLabel, paddingTop = 0, paddingBottom = 0, fontSize = 10, bottomTickOffset = 0 }) => {
	const hasMixedSign = domain.min < 0 && domain.max > 0;
	const midValue = hasMixedSign ? 0 : (domain.max + domain.min) / 2;
	const isZeroDomain = domain.min === 0 && domain.max === 0;

	// ── Vertical positioning — original effectiveHeight logic, untouched ──
	const effectiveHeight = height - paddingTop - paddingBottom;
	const range = domain.max - domain.min;
	const midPos =
		range === 0
			? paddingTop + effectiveHeight / 2
			: paddingTop + ((domain.max - midValue) / range) * effectiveHeight;
	const pos = {
		max: paddingTop,
		mid: midPos,
		min: paddingTop + effectiveHeight,
	};

	const ticks = isZeroDomain
		? [{ v: 0, y: pos.min }]
		: [
			{ v: domain.max, y: pos.max },
			{ v: midValue, y: pos.mid },
			{ v: domain.min, y: pos.min },
		];

	return (
		// ── Horizontal alignment fixes (new) ─────────────────────────────────
		// Removed: fixed width:50 and pr-2 — both caused the rightward gap.
		// The column now sizes to its content; labels grow leftward from the
		// tick mark which is pinned flush to the chart border via right:0.
		<div
			className="relative text-gray-500 font-medium"
			style={{
				height,
				flexShrink: 0,
				minWidth: 32, // prevents collapse before text renders
				maxWidth: 56, // prevents wide labels stealing chart space
				overflow: "visible",
			}}>
			{ticks.map(({ v, y }) => {
				const isBottomTick = v === domain.min;
				const transform =
					isBottomTick && bottomTickOffset
						? `translateY(calc(-50% - ${bottomTickOffset}px))`
						: "translateY(-50%)";
				return (
					<div
						key={v}
						className="absolute flex items-center"
						style={{
							right: 0, // tick mark flush to chart border
							top: y, // original pos.max / pos.mid / pos.min
							transform,
							whiteSpace: "nowrap",
						}}>
						{/* Label grows leftward from the tick mark */}
						<span style={{ fontSize, lineHeight: 1, marginRight: 2 }}>{formatDataLabel(v, labelFormat, true)}</span>

						{/* Tick mark — pinned at the chart border */}
						<span
							style={{
								display: "inline-block",
								width: 6,
								height: 1,
								flexShrink: 0,
								backgroundColor: v !== domain.min ? "#D1D5DB" : "transparent",
							}}
						/>
					</div>
				);
			})}
		</div>
	);
};
